import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { Subscription } from 'rxjs';
import {
  cleanForFilter,
  sortCaseInsensitive,
} from '../../../server/services/utils.service';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { TagService } from '../tag.service';
import { type OrgTaskWithUpdates, TaskService } from '../task.service';

@Component({
  selector: 'app-task-input',
  templateUrl: './task-input.component.html',
  styleUrls: ['./task-input.component.scss'],
  standalone: false,
})
export class TaskInputComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private taskService = inject(TaskService);
  private tagService = inject(TagService);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() orgId: string;
  @Input() spaceId: string;
  @Input() taskId: string;
  @Input() inputType: 'assignedTo' | 'tag';
  @Input() launchSource: 'title' | 'description' | undefined;

  showInput: 'assignedTo' | 'tag' | undefined;

  @Output() typeCharacter = new EventEmitter<'@' | '#'>();
  @Output() returnToCursor = new EventEmitter<'end' | undefined>();
  @Output() closeInput = new EventEmitter<undefined>();

  task: DeepReadonly<OrgTaskWithUpdates>;

  // Assign to
  assignedToControl = new FormControl('', { nonNullable: true });
  @ViewChild('assignedToInput')
  assignedToInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild(MatAutocompleteTrigger)
  assignedToAutocomplete: MatAutocompleteTrigger;
  assignedToOptions: { _id: string; title: string; selected: boolean }[] = [];
  assignedToOptionsFiltered: {
    _id: string;
    title: string;
    selected: boolean;
  }[] = [];

  // Tag
  tagsControl = new FormControl('', { nonNullable: true });
  @ViewChild('tagsInput')
  tagsInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild(MatAutocompleteTrigger) tagAutocomplete: MatAutocompleteTrigger;
  tagsOptions: { _id: string; title: string; selected: boolean }[] = [];
  tagsOptionsFiltered: {
    _id: string;
    title: string;
    selected: boolean;
  }[] = [];
  newTagString = '';

  temporarilyPreventBlurConsequences = false;

  error = false;

  subscriptions = new Subscription();

  ngOnInit(): void {
    const { orgId, spaceId, taskId } = this;
    const task = this.taskService.getTaskLocalSync({ orgId, spaceId, taskId });
    if (!task) {
      this.error = true;
      return;
    }
    this.task = task;
    if (this.inputType === 'assignedTo') {
      this.openAssignedToInput();
    } else if (this.inputType === 'tag') {
      this.openTagsInput();
    } else {
      throw new Error('Unexpected state in task input');
    }
    this.subscribeToAssignedToChanges();
    this.subscribeToTagsChanges();
  }

  // Assign to
  assignedToKeyPressIntercept(event: KeyboardEvent) {
    this.assignedToAndTagKeyPressIntercept(event, this.assignedToInput);
  }

  private subscribeToAssignedToChanges() {
    const sub = this.assignedToControl.valueChanges.subscribe((value) => {
      if (value === '@' && this.launchSource) {
        this.typeCharacter.emit('@');
      }
      this.filterAssignedToAutocomplete(value);
      if (this.assignedToControl.value.indexOf('\n') > -1)
        this.assignedToControl.setValue(value.replace('\n', ''));
    });
    this.subscriptions.add(sub);
  }

  openAssignedToInput(focusDelay?: number) {
    this.showInput = 'assignedTo';
    this.assignedToControl.setValue('');
    this.changeDetector.detectChanges(); // allows the assignedToInput below to be created so we can focus on it
    // Wait to set up blur events to prevent blur from occurring when launching from a menu
    setTimeout(() => {
      this.assignedToInput.nativeElement.focus();
      this.assignedToInput.nativeElement.onblur = () => {
        this.closeAfterBlur();
      };
    }, focusDelay ?? 0);

    const users = this.authUserService.getSpaceUserData({
      orgId: this.orgId,
      spaceId: this.spaceId,
    });
    this.assignedToOptions = users
      .map(({ _id, name }) => ({
        _id,
        title: name,
        selected: this.task.assignedTo === _id,
      }))
      .sort((a, b) => {
        // Put selected at top
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        // Sort by title
        return sortCaseInsensitive(a.title, b.title);
      });
    this.filterAssignedToAutocomplete('');
  }

  private filterAssignedToAutocomplete(value: string) {
    const filterValue = cleanForFilter(value);
    // Show options that start with the search query first
    const startsWithOptions = this.assignedToOptions.filter((option) =>
      cleanForFilter(option.title).startsWith(filterValue)
    );
    const otherOptions = this.assignedToOptions.filter(
      (option) =>
        !cleanForFilter(option.title).startsWith(filterValue) &&
        cleanForFilter(option.title).includes(filterValue)
    );

    this.assignedToOptionsFiltered = [...startsWithOptions, ...otherOptions];
  }

  assignedToSelected(event: MatAutocompleteSelectedEvent) {
    const { orgId, spaceId, _id: taskId } = this.task;
    const assignedTo =
      event.option.value === this.task.assignedTo ? null : event.option.value;

    this.taskService.updateTask({ orgId, spaceId, taskId }, { assignedTo });

    this.returnToCursor.emit();
  }

  // Tags
  tagsKeyPressIntercept(event: KeyboardEvent) {
    this.assignedToAndTagKeyPressIntercept(event, this.tagsInput);
  }

  private subscribeToTagsChanges() {
    const sub = this.tagsControl.valueChanges.subscribe((value) => {
      if (value === '#' && this.launchSource) {
        this.typeCharacter.emit('#');
      }
      this.filterTagsAutocomplete(value);
      if (this.tagsControl.value.indexOf('\n') > -1)
        this.tagsControl.setValue(value.replace('\n', ''));
    });
    this.subscriptions.add(sub);
  }

  openTagsInput(focusDelay?: number) {
    this.showInput = 'tag';
    this.tagsControl.setValue('');
    this.changeDetector.detectChanges(); // allows the tagsInput below to be created so we can focus on it
    // Wait to set up blur events to prevent blur from occurring when launching from a menu
    setTimeout(() => {
      this.tagsInput.nativeElement.focus();
      this.tagsInput.nativeElement.onblur = () => {
        this.closeAfterBlur();
      };
    }, focusDelay ?? 0);

    const tags = this.tagService.getAllTagsSync({
      orgId: this.orgId,
      spaceId: this.spaceId,
    });
    this.tagsOptions = tags
      .map(({ _id, name }) => ({
        _id,
        title: name,
        selected: this.task.tagIds.includes(_id),
      }))
      .sort((a, b) => {
        // Put selected at top
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        // Sort by title
        return sortCaseInsensitive(a.title, b.title);
      });
    this.filterTagsAutocomplete('');
  }

  private filterTagsAutocomplete(value: string) {
    const filterValue = cleanForFilter(value);
    // Show options that start with the search query first
    const startsWithOptions = this.tagsOptions.filter((option) =>
      cleanForFilter(option.title).startsWith(filterValue)
    );
    const otherOptions = this.tagsOptions.filter(
      (option) =>
        !cleanForFilter(option.title).startsWith(filterValue) &&
        cleanForFilter(option.title).includes(filterValue)
    );
    this.tagsOptionsFiltered = [...startsWithOptions, ...otherOptions];
    const potentialNewTag = value.trim();
    if (potentialNewTag.length) {
      const exactMatch = this.tagsOptions.find(
        (option) =>
          option.title.trim().toLowerCase() === potentialNewTag.toLowerCase()
      );
      if (!exactMatch) {
        this.tagsOptionsFiltered.push({
          _id: 'new.' + potentialNewTag,
          title: '+ Create new tag "' + potentialNewTag + '"',
          selected: false,
        });
      }
    }
  }

  tagsSelected(event: MatAutocompleteSelectedEvent) {
    const { orgId, spaceId } = this.task;
    const value: string = event.option.value;

    if (value.startsWith('new.')) {
      const name = value.substring(4);
      this.tagService.createTag({ orgId, spaceId, name }).subscribe({
        next: (tag) => {
          this.tagsSelected2(tag._id);
        },
      });
    } else {
      const tagId = value;
      this.tagsSelected2(tagId);
    }
  }

  tagsSelected2(tagId: string) {
    const { orgId, spaceId, _id: taskId } = this.task;

    if (!this.task.tagIds.includes(tagId)) {
      // Add tagId
      this.taskService.updateTask(
        { orgId, spaceId, taskId },
        { push: { tagIds: [tagId] } }
      );
    } else {
      // Remove tagId
      this.taskService.updateTask(
        { orgId, spaceId, taskId },
        { pull: { tagIds: [tagId] } }
      );
    }

    this.returnToCursor.emit();
  }

  closeAfterBlur(): void {
    if (this.temporarilyPreventBlurConsequences) {
      this.temporarilyPreventBlurConsequences = false;
      return;
    }
    // This is a fallback with a long timeout because it interferes with
    // delivering the click of the autocomplete panel. Normally this function
    // would never need to be called as the cleanUpTag will fire when
    // selecting a user
    setTimeout(() => this.closeInput.emit(), 500);
  }

  assignedToAndTagKeyPressIntercept(
    event: KeyboardEvent,
    element?: ElementRef<HTMLTextAreaElement>
  ) {
    if (event.key === '@' && this.showInput !== 'assignedTo') {
      this.temporarilyPreventBlurConsequences = true;
      return this.openAssignedToInput();
    }
    if (event.key === '#' && this.showInput !== 'tag') {
      this.temporarilyPreventBlurConsequences = true;
      return this.openTagsInput();
    }

    if (
      element &&
      element.nativeElement.selectionEnd === 0 &&
      (event.key === 'ArrowLeft' || event.key === 'Backspace')
    ) {
      // We delay to prevent the left arrow from being applied to the title element
      this.returnToCursor.emit('end');
    }

    if (event.key === 'Escape') {
      this.returnToCursor.emit();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
